import getFooter from 'api/Footer/getFooter';
import { getFranchiseBySlugAndYear } from 'api/Ranking/getFranchiseBySlugAndYear';
import type { HeaderNav } from 'interfaces/navigation/Header';
import type { FranchisePage } from 'interfaces/content/Ranking';
import { wellLogo } from 'icons/WellLogo';
import mockPageData from 'lib/mockData/page-data';
import type { GetStaticProps } from 'next';
import SimpleRankingPage from 'pages/SimpleRanking/SimpleRankingPage';
import setWellPageAdConfig from 'services/Ad/config/setWellPageAdConfig';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { setMockPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import { Microsites } from 'constants/constants';
import type { PreviewData } from 'interfaces/PreviewData';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface WellListProps {
  franchise: FranchisePage;
  subDomain: string;
}

const WellList = ({ franchise, subDomain }: WellListProps) => (
  <SimpleRankingPage
    franchiseList={franchise}
    subDomain={subDomain}
    hidePresentedBy
  />
);

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const platformCD: string = Microsites.Well;
    const { slug, region } = context.params as { slug: string; region: string };
    const { previewData } = context as { previewData: PreviewData };

    const franchise = await getFranchiseBySlugAndYear(slug, platformCD, undefined, previewData);
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_WELL', Microsites.Well, wellLogo),
      fallbackData.headerNav,
    );
    const pageAdConfig = safe(() => setWellPageAdConfig(franchise), fallbackData.pageAdConfig);
    const dataLayer = safe(() => setPageGtmConfig(franchise), fallbackData.dataLayer);
    const headData = safe(() => setHeadData(franchise, false, 'Fortune Well'), fallbackData.headData);

    return {
      props: {
        dataLayer,
        footer,
        franchise,
        headData,
        headerNav: menu,
        pageAdConfig,
        pianoPageConfig: setMockPianoConfig(),
        region,
        subDomain: menu?.subDomain,
      },
      revalidate: 60, // In seconds
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export const getStaticPaths = () => {
  const paths = [
    { params: { region: 'us', slug: 'best-places-families' } },
    { params: { region: 'non-us', slug: 'best-places-families' } },
  ];

  return {
    fallback: 'blocking',
    paths,
  };
};

export default WellList;
